import { CLOCK_LIMITS } from './clock-log.js';

/**
 * Gemide giriş ve parola özetinin gemideki penceresi (ADR 0154 §1). Tarihler gemi yerel tarihidir
 * (`YYYY-MM-DD`), bitiş günü dahildir; "an" UTC milisaniye olarak parametreyle gelir.
 *
 * Değişmez: geminin UTC farkı `CLOCK_LIMITS` içinde (−12:00 … +14:00) ne olursa olsun, `at`
 * anında geminin yerel günü atamanın içindeyse pencere açıktır. Bulut geminin saatini bilmeden
 * süzer; yanılırsa sonuç fazladan saklamadır, giriş bozulmaz.
 */
export interface CrewSpan {
  validFrom: string;
  validTo: string | null;
  deleted?: boolean;
}

const DAY_MS = 86_400_000;
const MINUTE_MS = 60_000;

/**
 * Gemi sunucusunun imzalı istek zamanının bulut saatinden en çok sapabileceği süre. Gemi saati
 * kayabilir; paketin kendisi imzalı ve tekrar gönderimi zararsız olduğu için geniş tutulur (mimari
 * belge §6.11). Parola özeti süzgeci de bu sınıra dayanır (ADR 0154 Ek).
 */
export const SHIP_CLOCK_TOLERANCE_MS = 24 * 3_600_000;

/** Bitiş gününün başından (00:00 UTC) kapanışa kadar: 1 gün + en geri fark (12 saat) = 36 saat. */
export const CREDENTIAL_CLOSE_AFTER_MS = DAY_MS - CLOCK_LIMITS.minOffset * MINUTE_MS;

/**
 * Atama `day` gününü kapsıyor mu (başlangıç ve bitiş dahil). Gemide giriş için gün geminin yerel
 * günüdür: gemi sunucusunun `actorRolesAt` sorgusuyla aynı kural (ADR 0012 §3).
 */
export function coversDay(span: Pick<CrewSpan, 'validFrom' | 'validTo'>, day: string): boolean {
  return span.validFrom <= day && (span.validTo === null || day <= span.validTo);
}

/**
 * Bitiş gününün dünyanın her yerinde bittiği an ("Anywhere on Earth"): bitiş + 1 gün 00:00 UTC
 * eksi en geri fark (−12:00), yani bitiş + 1 gün 12:00 UTC.
 */
export function credentialClosesAt(validTo: string): number {
  return Date.parse(`${validTo}T00:00:00.000Z`) + CREDENTIAL_CLOSE_AFTER_MS;
}

/**
 * Çekme süzgecinin anı (ADR 0154 Ek): bulutun anı ile geminin imzalı saatinin küçüğü, en çok
 * `SHIP_CLOCK_TOLERANCE_MS` geri. Gemi girişi geminin kendi saatinden hesaplanan güne bakar; geri
 * kalan gemi saatinde bulutun anı kapanışı geçmişken gemide hâlâ bitiş günü olabilir. İleri gemi
 * saati anı öne çekmez: saat sonra düzeltilirse gemi günü yeniden atamaya düşer. Gemi saati yoksa
 * bulutun anı. Değişmez: bulutun kabul ettiği her saat farkında, gemi günü atamadaysa pencere
 * açıktır (özellik testi).
 */
export function credentialPullAt(cloudAt: number, shipAt?: number | null): number {
  if (shipAt === undefined || shipAt === null || Number.isNaN(shipAt)) return cloudAt;
  return Math.max(Math.min(cloudAt, shipAt), cloudAt - SHIP_CLOCK_TOLERANCE_MS);
}

/**
 * Bu atama yüzünden kişinin parola özeti `at` anında gemide bulunur mu. Başlangıç için alt sınır
 * yok: hesap katılıştan önce gemide olmalı (ADR 0144 sahip kararı); gemi girişi tarihle engeller.
 * Silinmiş atama özet vermez.
 */
export function credentialOnShip(span: CrewSpan, at: number): boolean {
  if (span.deleted === true) return false;
  return span.validTo === null || at < credentialClosesAt(span.validTo);
}

/** Kişinin bir gemideki atamalarından biri özeti gemide tutuyor mu. */
export function anyCredentialOnShip(spans: readonly CrewSpan[], at: number): boolean {
  return spans.some((span) => credentialOnShip(span, at));
}
